import React, { useCallback, useEffect, useState } from "react";
import Button from "@amzn/meridian/button";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import CalendarTimeframeSelect from "@amzn/meridian/calendar-timeframe-select";
import CalendarWeekView from "@amzn/meridian/calendar-week-view";
import ButtonGroup, { ButtonOption } from "@amzn/meridian/button-group";
import { Space } from "../space/Space";
import { MailAlerts } from "../mail/MailAlerts";
import { useAppSelector } from "../../app/store/hooks";
import { useTable } from "../table/hooks";
import { StatusEnum, useLazyGetOverdueMailsQuery } from "../../endpoints/getArtifacts";
import { useLazyGetCalendarInfoQuery } from "../../endpoints/getArtifactsCount";
import { getTwoMonthsAhead, getTwoMonthsBack, now, renderEvents } from "./utils";
import { CalendarTable } from "./calendarTable";
import { DEFAULT_LIMIT_PER_REQUEST } from "../../constants/defaultsConstants";

export const CalendarComponent: React.FC = () => {
    const [componentMounted, setComponentMounted] = useState(false);
    const { alias } = useAppSelector(state => state.auth.user);
    const [date, setDate] = useState(now);
    const startDate = getTwoMonthsBack(date);
    const endDate = getTwoMonthsAhead(date);
    const onClickNow = useCallback(() => setDate(now), []);
    const [calendarType, setCalendarType] = useState("my-calendar");
    const [getCalendar, { data: calendarData }] = useLazyGetCalendarInfoQuery();
    const [getOverdueMails, { data: overdueMails }] = useLazyGetOverdueMailsQuery();
    const [calendarEvents, setCalendarEvents] = useState(calendarData?.artifactCountMap ?? {});
    const totalOverdueCount = overdueMails?.data?.totalCount || 0;
    const [calendarTableQuery, setCalendarTableQuery] = useState<any>(null);
    const { isCalendarVisible } = useTable();

    useEffect(() => {
        getCalendar({
            useCase: "GET_AGGREGATED_DOCUMENTS_BY_DUE_DATE",
            businessUseCase: "SMART",
            queryParams: {
                startDate: startDate,
                endDate: endDate,
                ...(calendarType === "my-calendar" ? { assignedTo: alias } : null),
            },
        });
        getOverdueMails({
            useCase: "GET_DOCUMENTS",
            businessUseCase: "SMART",
            queryParams: {
                limitPerRequest: DEFAULT_LIMIT_PER_REQUEST,
                requestNumber: 1,
                ...(calendarType === "my-calendar" ? { assignedTo: alias } : null),
                status: [StatusEnum.OVERDUE],
            },
        });
    }, [startDate, endDate, calendarType, componentMounted]);

    useEffect(() => {
        if (calendarData) {
            setCalendarEvents(calendarData?.artifactCountMap);
        }
    }, [calendarData, calendarType]);

    useEffect(() => {
        setComponentMounted(true);
    }, []);

    useEffect(() => {
        setCalendarTableQuery(null);
    }, [date]);

    return (
        <>
            <Row width={"100%"} widths={["fill", "fit"]}>
                <Text type={"h400"}>Upcoming dues and reminders</Text>
                <ButtonGroup value={calendarType} onChange={setCalendarType}>
                    <ButtonOption value="my-calendar">My calendar</ButtonOption>
                    <ButtonOption value="team-calendar">Team calendar</ButtonOption>
                </ButtonGroup>
            </Row>
            <Space size={"xxs"} />
            {totalOverdueCount > 0 && (
                <MailAlerts
                    title={`${totalOverdueCount} mails are overdue`}
                    description={`You have ${totalOverdueCount} mails that remain open after their due date.`}
                    detailsLink={
                        calendarType === "my-calendar" ? "overdue-mails-to-me" : "overdue-mails"
                    }
                />
            )}

            <Space size={"xxs"} />
            <Column
                type="outline"
                heights={["fit", "fill"]}
                spacing="none"
                height={600}
                width="100%"
            >
                <Row spacingInset="400" alignmentHorizontal="justify" widths={[100, "fit", 100]}>
                    <Button type="secondary" onClick={onClickNow}>
                        This week
                    </Button>
                    <CalendarTimeframeSelect type="week" value={date} onChange={setDate} />
                    <span />
                </Row>
                <CalendarWeekView
                    viewDate={date}
                    now={now}
                    renderDay={day => renderEvents(day, calendarEvents, setCalendarTableQuery)}
                />
            </Column>
            <Space size={"m"} />
            {calendarTableQuery && isCalendarVisible && (
                <CalendarTable
                    calendarTableQuery={calendarTableQuery}
                    calendarType={calendarType}
                    setCalendarTableQuery={setCalendarTableQuery}
                    alias={alias}
                />
            )}
        </>
    );
};
